import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setCategory(null);
    Promise.all([
      api.get("/jewellery/categories").then((r) => r.data),
      api.get(`/products?category=${slug}`).then((r) => r.data),
      api.get("/config").then((r) => r.data).catch(() => ({})),
    ]).then(([cats, prods, cfg]) => {
      const cat = cats.find((c) => c.slug === slug);
      if (!cat) setNotFound(true);
      setCategory(cat || null);
      setProducts(prods);
      setConfig(cfg);
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="category-not-found">
        <h1 className="font-serif text-4xl text-[#FBF9F5]">Category not found</h1>
        <Link to="/jewellery" className="text-[#C5A059] text-sm mt-4 inline-block">Back to Jewellery</Link>
      </div>
    );
  }

  const wa = config.whatsapp_jewellery;

  return (
    <div data-testid={`category-page-${slug}`} className="pt-28 sm:pt-36">
      <SEO title={category ? `${category.name} — Ramesh Kapoor and Sons Jewellers` : "Jewellery"}
        description={category ? `${category.name} — wholesale and retail jewellery from Ramesh Kapoor and Sons Jewellers Pvt. Ltd., Meerut and Delhi.` : ""} />

      <header className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-14">
        <Eyebrow>
          <Link to="/jewellery" className="hover:text-[#FBF9F5] transition-colors">Jewellery</Link>
          <span className="mx-2 text-[#94A3B8]">/</span> {category?.name || "…"}
        </Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4">
          {category?.name}
        </h1>
        <p className="text-sm text-[#94A3B8] leading-relaxed mt-6 max-w-2xl">
          Final jewellery pricing depends on the current metal rate, net weight, purity, making charges, stone or
          diamond value, applicable taxes, and other disclosed charges. Request the current price for any piece
          and we will confirm it with the day's rate.
        </p>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-28">
        {products.length === 0 ? (
          <div data-testid="category-empty-state" className="border border-dashed border-[#C5A059]/40 p-14 text-center">
            <p className="font-serif text-2xl text-[#FBF9F5]">Stock for this category is being catalogued.</p>
            <p className="text-sm text-[#94A3B8] mt-3 max-w-lg mx-auto leading-relaxed">
              Our {category?.name?.toLowerCase()} range is available at the showroom and wholesale offices.
              Request the catalogue or book an appointment to view current designs.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link to="/jewellery/catalogue" data-testid="category-request-catalogue-btn"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#C5A059] text-[#060A12] px-8 py-4 hover:bg-[#d6b678] transition-colors duration-300">
                Request Catalogue
              </Link>
              <Link to="/jewellery/appointment" data-testid="category-appointment-btn"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-white/20 text-[#FBF9F5] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                Book an Appointment
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05} className="group border border-white/10 bg-[#0B1320] hover:border-[#C5A059]/40 transition-colors duration-500">
                <div className="aspect-square overflow-hidden bg-[#060A12]">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-serif italic text-2xl text-[#C5A059]/40">Photograph to come</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-xl text-[#FBF9F5]">{p.name}</h3>
                    {p.code && <span className="text-[10px] tracking-[0.15em] text-[#C5A059] border border-[#C5A059]/30 px-2 py-1 uppercase">{p.code}</span>}
                  </div>
                  <dl className="mt-4 space-y-1.5 text-xs text-[#94A3B8]">
                    {p.metal && <div className="flex justify-between"><dt>Metal</dt><dd className="text-[#FBF9F5]/80">{p.metal}</dd></div>}
                    {p.purity && <div className="flex justify-between"><dt>Purity</dt><dd className="text-[#FBF9F5]/80">{p.purity}</dd></div>}
                    {p.weight_range && <div className="flex justify-between"><dt>Weight range</dt><dd className="text-[#FBF9F5]/80">{p.weight_range}</dd></div>}
                    {p.stones && <div className="flex justify-between"><dt>Stones</dt><dd className="text-[#FBF9F5]/80">{p.stones}</dd></div>}
                    <div className="flex justify-between"><dt>Availability</dt><dd className={p.available ? "text-emerald-400" : "text-red-400"}>{p.available ? "Available" : "On request"}</dd></div>
                  </dl>
                  <div className="flex gap-2 mt-6">
                    <Link to={`/contact?type=wholesale_jewellery&product=${encodeURIComponent(p.code || p.name)}`}
                      data-testid={`product-price-${p.id}`}
                      className="flex-1 text-center text-[10px] uppercase tracking-[0.15em] font-semibold bg-[#C5A059] text-[#060A12] px-2 py-3 hover:bg-[#d6b678] transition-colors duration-300">
                      Request Current Price
                    </Link>
                    {wa && (
                      <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Enquiry about ${p.name} (${p.code || "no code"})`)}`}
                        target="_blank" rel="noreferrer" data-testid={`product-whatsapp-${p.id}`}
                        className="flex-1 text-center text-[10px] uppercase tracking-[0.15em] font-semibold border border-white/20 text-[#FBF9F5] px-2 py-3 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                        WhatsApp
                      </a>
                    )}
                    <Link to="/jewellery/appointment" data-testid={`product-appointment-${p.id}`}
                      className="flex-1 text-center text-[10px] uppercase tracking-[0.15em] font-semibold border border-white/20 text-[#FBF9F5] px-2 py-3 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                      Appointment
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
